import { useState } from "react";
import { useParams } from "react-router";
import { Button, Card, Input, Modal, ModalFooter } from "@insights/ui";
import { Trash2 } from "lucide-react";
import { AppShell } from "../../../shared/layout";
import { PageHeader } from "../../../shared/components";
import { useGeneralSettings } from "../hooks/useGeneralSettings";
import "../styles/general.css";

export function GeneralPage() {
  const { workspaceId = "" } = useParams();
  return <GeneralSettings key={workspaceId} workspaceId={workspaceId} />;
}

function GeneralSettings({ workspaceId }: { workspaceId: string }) {
  const { query, rename, remove } = useGeneralSettings(workspaceId);
  const [draft, setDraft] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const name = draft ?? query.data?.name ?? "";
  const canRename = ["owner", "admin"].includes(query.data?.role ?? "");
  return (
    <AppShell>
      <PageHeader eyebrow="Settings" title="General" />
      <div className="workspace-general">
        {query.error && (
          <p role="alert" className="error">
            {query.error.message}
          </p>
        )}
        <Card className="workspace-general-card">
          <h2>Workspace name</h2>
          <p className="muted">
            Choose the name shown to everyone in this workspace.
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              rename.mutate(name.trim(), { onSuccess: () => setDraft(null) });
            }}
          >
            <label>
              Name
              <Input
                value={name}
                maxLength={100}
                disabled={!canRename || rename.isPending}
                onChange={(event) => setDraft(event.target.value)}
                required
              />
            </label>
            {rename.error && (
              <p role="alert" className="error">
                {rename.error.message}
              </p>
            )}
            {canRename && (
              <div>
                <Button
                  type="submit"
                  disabled={
                    rename.isPending ||
                    !name.trim() ||
                    name.trim() === query.data?.name
                  }
                >
                  {rename.isPending ? "Saving…" : "Save name"}
                </Button>
              </div>
            )}
          </form>
        </Card>
        {query.data?.role === "owner" && (
          <Card className="workspace-general-card workspace-general-danger">
            <h2>Delete workspace</h2>
            <p>
              Remove this workspace for everyone, including its projects, build
              history, features and attachments. This cannot be undone.
            </p>
            <p className="muted">
              Any paid subscription must end first. Stored data is cleaned up in
              the background; existing backups follow their retention policy.
            </p>
            <div>
              <Button
                color="danger"
                icon={<Trash2 size={16} />}
                onClick={() => {
                  setConfirmation("");
                  remove.reset();
                  setConfirmOpen(true);
                }}
              >
                Delete workspace
              </Button>
            </div>
          </Card>
        )}
      </div>
      <Modal
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!remove.isPending) setConfirmOpen(open);
        }}
        title="Delete workspace"
        description="Permanently remove this workspace for all members."
      >
        <p>
          Everyone will lose access to <strong>{query.data?.name}</strong>. Its
          data will be permanently deleted. This cannot be undone.
        </p>
        <label className="workspace-general-confirm">
          Type “{query.data?.name}” to confirm
          <Input
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            disabled={remove.isPending}
            autoComplete="off"
          />
        </label>
        {remove.error && (
          <p role="alert" className="error">
            {remove.error.message}
          </p>
        )}
        <ModalFooter>
          <Button
            color="neutral"
            disabled={remove.isPending}
            onClick={() => setConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            disabled={remove.isPending || confirmation !== query.data?.name}
            onClick={() => {
              remove.mutate(confirmation, {
                onSuccess: () => window.location.assign("/"),
              });
            }}
          >
            {remove.isPending ? "Deleting…" : "Delete workspace"}
          </Button>
        </ModalFooter>
      </Modal>
    </AppShell>
  );
}
